/* eslint-disable @typescript-eslint/no-explicit-any */
/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/

export abstract class DTO {
    id = '';
}

export interface HttpResponse<T> extends Response {
    parsedBody?: T;
}

class ErrorResponse {
    errorCode?: number;
    message?: string;
    status?: number;
    timestamp?: string;
    detail?: string;
}

export class BaseService {
    //url = "https://www.dynamiciotdata.com:8081";
    url = 'https://localhost:8080';
    jwt: string | null;
    orgid: string | null;

    constructor() {
        this.jwt = localStorage.getItem('vision-jwt');
        this.orgid = localStorage.getItem('orgId');

        if (this.jwt == null || this.orgid == null) {
            window.location.href = 'https://www.dynamiciotdata.com/login.html';
        }

        // If running on same server ( ie dev then set to local host)
        if (window.location.host == 'localhost') {
            this.url = 'https://localhost:8080';
        } else {
            this.url = 'https://www.dynamiciotdata.com:8080';
        }

        //this.url = 'https://www.dynamiciotdata.com:8080';
    }

    async http<T>(request: RequestInfo): Promise<HttpResponse<T>> {
        const response: HttpResponse<T> = await fetch(request);

        try {
            // may error if there is no body
            response.parsedBody = await response.json();
        } catch (ex) {
            console.log(ex);
        }

        if (!response.ok) {
            const message = (response.parsedBody as ErrorResponse).message;
            const detail = (response.parsedBody as ErrorResponse).detail;
            dhtmlx.alert({
                title: 'Error Encountered',
                text: message + ':' + detail,
                type: 'alert-error',
            });
        }
        return response;
    }

    public getParsedBody<T>(response: HttpResponse<T>): T {
        if (response.parsedBody === undefined) {
            throw new Error('Response did not contain a body');
        }

        return response.parsedBody;
    }

    async get<T>(path: string, args: RequestInit = { method: 'get', headers: this.headers() }): Promise<HttpResponse<T>> {
        return await this.http<T>(new Request(path, args));
    }

    async post<T>(
        path: string,
        body: any,
        args: RequestInit = {
            method: 'post',
            body: JSON.stringify(body),
            headers: this.headers(),
        }
    ): Promise<HttpResponse<T>> {
        return await this.http<T>(new Request(path, args));
    }

    async post2<T>(
        path: string,
        body: any,
        args: RequestInit = {
            method: 'post',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + this.jwt,
            },
        }
    ): Promise<HttpResponse<T>> {
        return await this.http<T>(new Request(path, args));
    }

    async put<T>(
        path: string,
        body: any,
        args: RequestInit = {
            method: 'put',
            body: JSON.stringify(body),
            headers: this.headers(),
        }
    ): Promise<HttpResponse<T>> {
        return await this.http<T>(new Request(path, args));
    }

    async delete<T>(
        path: string,
        body: any,
        args: RequestInit = {
            method: 'delete',
            body: JSON.stringify(body),
            headers: this.headers(),
        }
    ): Promise<HttpResponse<T>> {
        return await this.http<T>(new Request(path, args));
    }

    public streamheaders() {
        return {
            'Content-Type': 'application/octet-stream',
            Authorization: 'Bearer ' + this.jwt,
        };
    }

    public headers() {
        return {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + this.jwt,
        };
    }

    public downloadXLSX(path: string, filename: string) {
        const url = this.url + '/api/' + this.orgid + path;
        fetch(url, {
            method: 'GET',
            headers: this.streamheaders(),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                return response.blob();
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename; // Set the filename
                document.body.appendChild(a); // Append to body
                a.click(); // Trigger the download

                // Clean up
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            })
            .catch((error) => {
                console.error('Error downloading the XLSX:', error);
            });
    }

    public downloadXLSXPost(path: string, filename: string, body: any, done: () => void) {
        const url = this.url + '/api/' + this.orgid + path;
        fetch(url, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(body),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                return response.blob();
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename; // Set the filename
                document.body.appendChild(a); // Append to body
                a.click(); // Trigger the download

                // Clean up
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
                if (done) {
                    done();
                }
            })
            .catch((error) => {
                console.error('Error downloading the XLSX:', error);
            });
    }

    public downloadPDFPost(path: string, filename: string, body: any, done: () => void) {
        const url = this.url + '/api/' + this.orgid + path;
        fetch(url, {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(body),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                return response.blob(); // Convert the response to a blob
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                // Create an anchor element to download the PDF
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename; // Set the filename
                document.body.appendChild(a); // Append to body
                a.click(); // Trigger the download

                // Clean up
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
                if (done) {
                    done();
                }
            })
            .catch((error) => {
                console.error('Error downloading the PDF:', error);
            });
    }

    public downloadPDF(path: string, filename: string) {
        const url = this.url + '/api/' + this.orgid + path;
        fetch(url, {
            method: 'GET',
            headers: this.streamheaders(),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }
                return response.blob(); // Convert the response to a blob
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                // Create an anchor element to download the PDF
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename; // Set the filename
                document.body.appendChild(a); // Append to body
                a.click(); // Trigger the download

                // Clean up
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            })
            .catch((error) => {
                console.error('Error downloading the PDF:', error);
            });
    }
}
